// ============================================
// Path: /frontend/src/components/ListingsGrid.jsx
// Purpose: Reusable listings grid with search, sort, and filter
// Code to help filter results
// ============================================

import { useState, useEffect } from 'react';
import { ListingCard } from './ListingCard';
import { Skeleton } from './ui/skeleton';
import { Search } from 'lucide-react';

export default function ListingsGrid({
  listings = [],
  loading = false,
  title = "Listings",
  showFilters = true,
  defaultCategory = 'all',
  defaultSort = 'newest',
  emptyMessage = "No listings available"
}) {
  const [filteredListings, setFilteredListings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState(defaultSort);
  const [filterCategory, setFilterCategory] = useState(defaultCategory);

  // Apply filters and sorting
  useEffect(() => {
    let result = [...listings];

    // Search filter
    if (searchTerm.trim()) {
      result = result.filter(listing =>
        listing.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        listing.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (filterCategory !== 'all') {
      result = result.filter(listing => listing.category === filterCategory);
    }

    // Sort
    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'oldest':
        result.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      default:
        break;
    }

    setFilteredListings(result);
  }, [listings, searchTerm, sortBy, filterCategory]);

  // Loading skeleton
  if (loading) {
    return (
      <div>
        <h2 className="text-2xl font-bold mb-6">{title}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-64 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Title */}
      <h2 className="text-2xl font-bold mb-6">{title}</h2>

      {/* Search, Filter, and Sort Controls */}
      {showFilters && listings.length > 0 && (
        <div className="mb-6 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search listings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Filter and Sort Row */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Category Filter */}
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="VEHICLE">Vehicles</option>
              <option value="PROPERTY">Properties</option>
            </select>

            {/* Sort Dropdown */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>

            {/* Results Count */}
            <div className="flex items-center text-sm text-muted-foreground sm:ml-auto">
              Showing {filteredListings.length} of {listings.length} listings
            </div>
          </div>
        </div>
      )}

      {/* Listings Grid or Empty State */}
      {filteredListings.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <p>{searchTerm || filterCategory !== 'all' ? 'No listings match your filters' : emptyMessage}</p>
          {(searchTerm || filterCategory !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterCategory('all');
              }}
              className="mt-4 text-primary underline hover:no-underline"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredListings.map((listing) => (
            <ListingCard
              key={listing.id}
              id={listing.id}
              title={listing.title}
              price={`KES ${listing.price.toLocaleString('en-US')}`}
              location={`${listing.subCounty}, ${listing.county}`}
              imageUrl={listing.imageUrl}
              type={listing.subType || listing.type}
              isPromoted={listing.isFeatured && new Date(listing.featuredUntil) > new Date()}
            />
          ))}
        </div>
      )}
    </div>
  );
}